if (!customElements.get('return-label-form')) {
  class ReturnLabelForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.message = this.querySelector('[data-return-label-message]');
      this.dialog = this.querySelector('[data-return-label-dialog]');
      this.button = this.querySelector('button[type="submit"]');
      this.buttonText = this.querySelector('[data-return-label-button-text]');
      this.pendingFormData = null;
      this.returnLabelVariantId = Number(this.dataset.returnLabelVariantId);
      this.endpoint = this.dataset.endpoint;
      this.strings = this.getStrings();
    }

    connectedCallback() {
      if (!this.form || !this.endpoint || !this.returnLabelVariantId) return;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      if (this.dialog) {
        this.dialog.addEventListener('click', this.handleDialogClick.bind(this));
        this.dialog.addEventListener('click', this.handleDialogBackdropClick.bind(this));
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.pendingFormData = new FormData(this.form);
      this.setMessage('');
      this.setLoading(true);

      try {
        const cart = await this.fetchJson('/cart.js');

        if (this.cartHasNonReturnLabelItems(cart)) {
          this.setLoading(false);
          this.showCartDialog();
          return;
        }

        await this.clearCart();
        await this.continueReturnLabelFlow(this.pendingFormData);
      } catch (error) {
        this.showError(error);
        this.setLoading(false);
      }
    }

    async handleDialogClick(event) {
      const button = event.target.closest('button[data-action]');
      if (!button) return;

      if (button.dataset.action === 'cancel') {
        this.closeDialog();
        return;
      }

      if (button.dataset.action !== 'clear-and-continue') return;

      this.closeDialog();
      this.setMessage('');
      this.setLoading(true);

      try {
        await this.clearCart();
        await this.continueReturnLabelFlow(this.pendingFormData);
      } catch (error) {
        this.showError(error);
        this.setLoading(false);
      }
    }

    handleDialogBackdropClick(event) {
      if (event.target === this.dialog) this.closeDialog();
    }

    cartHasNonReturnLabelItems(cart) {
      return (cart.items || []).some((item) => Number(item.variant_id) !== this.returnLabelVariantId);
    }

    showCartDialog() {
      if (this.dialog && typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
        return;
      }

      if (window.confirm(this.strings.cartConflictMessage)) {
        this.setLoading(true);
        this.clearCart()
          .then(() => this.continueReturnLabelFlow(this.pendingFormData))
          .catch((error) => {
            this.showError(error);
            this.setLoading(false);
          });
      }
    }

    closeDialog() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    async continueReturnLabelFlow(formData) {
      if (!formData) throw new Error(this.strings.missingFormError);

      const email = String(formData.get('email') || '').trim().toLowerCase();
      const start = await this.fetchJson(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderNumber: String(formData.get('orderNumber') || '').trim(),
          email
        })
      });

      const variantId = Number(start.variantId) || this.returnLabelVariantId;
      const lineItemProperties = start.lineItemProperties || {
        _original_order_name: start.originalOrderName || start.orderName,
        _original_order_id: start.originalOrderId,
        _return_email: start.returnEmail || email,
        _return_token: start.returnToken
      };

      await this.fetchJson('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: lineItemProperties
            }
          ]
        })
      });

      window.location.href = '/checkout';
    }

    async clearCart() {
      await this.fetchJson('/cart/clear.js', { method: 'POST' });
    }

    async fetchJson(url, options) {
      const response = await fetch(url, options);
      const text = await response.text();
      let data = {};

      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        if (!response.ok) throw new Error(this.strings.genericError);
        throw error;
      }

      if (!response.ok) {
        throw new Error(this.translateError(data.error || data.description || data.message));
      }

      return data;
    }

    getStrings() {
      const stringsEl = this.querySelector('[data-return-label-strings]');
      const fallback = {
        genericError: 'Something went wrong. Please try again.',
        missingFormError: 'Enter order number and email again.',
        invalidEmailError: 'Enter a valid email address.',
        invalidOrderNumberError: 'Enter the order number in the format SH1234 or 1234.',
        orderNotFoundError: 'We could not find an order with those details.',
        emailMismatchError: 'The email address does not match the order.',
        duplicateReturnLabelError: 'A return label has already been ordered for this order.',
        loadingLabel: 'Checking...',
        buttonLabel: 'Buy return label',
        cartConflictMessage: 'Return labels cannot be combined with other products. Empty the cart first and try again.'
      };

      if (!stringsEl) return fallback;

      try {
        return { ...fallback, ...JSON.parse(stringsEl.textContent) };
      } catch (error) {
        return fallback;
      }
    }

    translateError(message) {
      if (!message) return this.strings.genericError;

      const normalizedMessage = String(message).trim().toLowerCase();
      const messages = {
        'email must be a valid email address': this.strings.invalidEmailError,
        'ordernumber must be sh1234 or 1234': this.strings.invalidOrderNumberError,
        'order not found': this.strings.orderNotFoundError,
        'email does not match order': this.strings.emailMismatchError,
        'retursedel har redan best\u00e4llts f\u00f6r den h\u00e4r ordern.': this.strings.duplicateReturnLabelError,
        'invalid json body': this.strings.genericError,
        'method not allowed': this.strings.genericError,
        'not found': this.strings.genericError,
        unauthorized: this.strings.genericError
      };

      if (messages[normalizedMessage]) return messages[normalizedMessage];
      if (/[\u00e5\u00e4\u00f6\u00c5\u00c4\u00d6]/.test(message)) return String(message);

      return this.strings.genericError;
    }

    setLoading(isLoading) {
      if (this.button) {
        this.button.disabled = isLoading;
        this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      if (this.buttonText) {
        this.buttonText.textContent = isLoading ? this.strings.loadingLabel : this.strings.buttonLabel;
      }
    }

    setMessage(message, type = '') {
      if (!this.message) return;
      this.message.textContent = message;
      this.message.dataset.type = type;
    }

    showError(error) {
      const message = error instanceof Error ? error.message : this.strings.genericError;
      this.setMessage(message, 'error');
    }
  }

  customElements.define('return-label-form', ReturnLabelForm);
}
